import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AeroCrsAgency,
  aerocrsService,
  AeroCrsApiStatus,
  AeroCrsBooking,
  AeroCrsCustomerSearchResult,
  AeroCrsDestination,
  AeroCrsFlight,
  AeroCrsPassengerInput,
  AeroCrsSelectedCustomer
} from '../services/aerocrsService';

export type AeroCrsPassengerRow = AeroCrsPassengerInput & { id: string };
export type AeroCrsPassengerRole = 'ADULT' | 'CHILD' | 'INFANT';
export type AeroCrsPassengerManifestRow = AeroCrsPassengerRow & {
  passengerType: AeroCrsPassengerRole;
  roleIndex: number;
  roleLabel: string;
  requiresDateOfBirth: boolean;
};

const makePassengerRow = (index: number): AeroCrsPassengerRow => ({
  id: `pax-${Date.now()}-${index}`,
  firstName: '',
  lastName: '',
  gender: '',
  dateOfBirth: '',
  nationality: '',
  passportNumber: '',
  phone: '',
  email: '',
});

const buildPassengerRows = (count: number): AeroCrsPassengerRow[] =>
  Array.from({ length: Math.max(1, count) }, (_, index) => makePassengerRow(index));

const buildPassengerManifest = (
  passengers: AeroCrsPassengerRow[],
  adults: number,
  children: number,
  infants: number
): AeroCrsPassengerManifestRow[] => {
  let adultSeen = 0;
  let childSeen = 0;
  let infantSeen = 0;

  return passengers.map((row, index) => {
    let passengerType: AeroCrsPassengerRole = 'ADULT';
    let roleIndex = index + 1;

    if (index < adults) {
      adultSeen += 1;
      passengerType = 'ADULT';
      roleIndex = adultSeen;
    } else if (index < adults + children) {
      childSeen += 1;
      passengerType = 'CHILD';
      roleIndex = childSeen;
    } else {
      infantSeen += 1;
      passengerType = 'INFANT';
      roleIndex = infantSeen;
    }

    return {
      ...row,
      passengerType,
      roleIndex,
      roleLabel: `${passengerType === 'ADULT' ? 'Adult' : passengerType === 'CHILD' ? 'Child' : 'Infant'} ${roleIndex}`,
      requiresDateOfBirth: passengerType !== 'ADULT',
    };
  });
};

const toNonNegativeInt = (value: number, fallback = 0) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(0, Math.trunc(parsed));
};

const extractBookingReference = (bookingLike: any): string => {
  const direct = String(bookingLike?.bookingReference || bookingLike?.bookingId || '').trim();
  if (direct) return direct;

  const summary = String(bookingLike?.rawPayload?.summary || bookingLike?.summary || '').trim();
  return (
    summary.match(/Reservations\s+([0-9]{6,}|[0-9]{4,}[A-Z]\d{2,})\s+/i)?.[1]
    || summary.match(/\b([0-9]{6,}|[0-9]{4,}[A-Z]\d{2,})\b/i)?.[1]
    || ''
  );
};

const normalizeBookingReference = <T extends AeroCrsBooking | null | undefined>(bookingLike: T): T => {
  if (!bookingLike) return bookingLike;
  const bookingReference = extractBookingReference(bookingLike);
  if (!bookingReference || bookingLike.bookingReference === bookingReference) {
    return bookingLike;
  }
  return {
    ...bookingLike,
    bookingReference,
    bookingId: bookingLike.bookingId || bookingReference,
  };
};

export const useAeroCrsBookingDesk = () => {
  const [status, setStatus] = useState<AeroCrsApiStatus | null>(null);
  const [agencies, setAgencies] = useState<AeroCrsAgency[]>([]);
  const [destinations, setDestinations] = useState<AeroCrsDestination[]>([]);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  const [isLoadingBooking, setIsLoadingBooking] = useState(false);
  const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
  const [isSelectingCustomer, setIsSelectingCustomer] = useState(false);
  const [isSavingLimits, setIsSavingLimits] = useState(false);
  const [lastSearchTime, setLastSearchTime] = useState<string | null>(null);
  const [selectedBookingReference, setSelectedBookingReference] = useState<string | null>(null);
  const [selectedFlight, setSelectedFlight] = useState<AeroCrsFlight | null>(null);
  const [searchResults, setSearchResults] = useState<AeroCrsFlight[]>([]);
  const [booking, setBooking] = useState<AeroCrsBooking | null>(null);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState<AeroCrsCustomerSearchResult[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<AeroCrsSelectedCustomer | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [limitForm, setLimitForm] = useState({
    ticketDate: '',
    ticketTime: '',
    paymentDate: '',
    paymentTime: '',
  });
  const [form, setForm] = useState({
    from: '',
    to: '',
    departureDate: '',
    returnDate: '',
    adults: 1,
    children: 0,
    infants: 0,
    companyName: '',
    bookingName: '',
    flightType: 'SCHEDULED' as 'SCHEDULED' | 'CHARTER',
    chargeType: 'ADT',
    currency: 'USD',
  });
  const [passengers, setPassengers] = useState<AeroCrsPassengerRow[]>(buildPassengerRows(1));

  const totalPassengers = useMemo(
    () => Math.max(1, toNonNegativeInt(form.adults, 1) + toNonNegativeInt(form.children) + toNonNegativeInt(form.infants)),
    [form.adults, form.children, form.infants]
  );

  const seatBearingPassengers = useMemo(
    () => Math.max(1, toNonNegativeInt(form.adults, 1) + toNonNegativeInt(form.children)),
    [form.adults, form.children]
  );

  const loadStatus = useCallback(async () => {
    setIsLoadingStatus(true);
    try {
      const [statusData, destinationData, agencyData] = await Promise.all([
        aerocrsService.getStatus(),
        aerocrsService.getDestinations().catch(() => []),
        aerocrsService.getAgencies().catch(() => []),
      ]);
      setStatus(statusData);
      setDestinations(destinationData);
      setAgencies(agencyData);
      setLastError(null);
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Failed to load AeroCRS status';
      setLastError(message);
    } finally {
      setIsLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    const intervalMs = status?.queueBusy ? 5000 : 30000;
    const timer = window.setInterval(() => {
      void loadStatus();
    }, intervalMs);

    return () => window.clearInterval(timer);
  }, [loadStatus, status?.queueBusy]);

  useEffect(() => {
    if (!agencies.length) return;
    setForm((current) => {
      if (current.companyName.trim()) return current;
      const defaultAgency = agencies.find((item) => item.name === 'No Agency - Direct Client');
      if (!defaultAgency) return current;
      return {
        ...current,
        companyName: defaultAgency.name,
      };
    });
  }, [agencies]);

  useEffect(() => {
    setPassengers((current) => {
      const nextCount = totalPassengers;
      if (current.length === nextCount) return current;
      if (current.length > nextCount) return current.slice(0, nextCount);
      return [...current, ...buildPassengerRows(nextCount - current.length)];
    });
  }, [totalPassengers]);

  useEffect(() => {
    const parseLimit = (value?: string | null) => {
      const raw = String(value || '').trim();
      if (!raw) return { date: '', time: '' };
      const match = raw.match(/^(.+?)\s+(\d{2}:\d{2})$/);
      if (!match) return { date: raw, time: '' };
      return { date: match[1], time: match[2] };
    };

    if (!booking) return;
    const ticket = parseLimit(booking.ticketTimeLimit);
    const payment = parseLimit(booking.paymentTimeLimit);
    setLimitForm({
      ticketDate: ticket.date,
      ticketTime: ticket.time,
      paymentDate: payment.date,
      paymentTime: payment.time,
    });
  }, [booking]);

  const destinationOptions = useMemo(
    () => destinations.map((item, index) => ({
      id: `${item.destinationId || item.code || 'dest'}-${index}`,
      value: item.code,
      label: `${item.code} - ${item.name}`,
    })),
    [destinations]
  );

  const agencyOptions = useMemo(
    () => agencies.map((item) => ({ value: item.name, label: item.name, id: item.id })),
    [agencies]
  );

  const passengerManifest = useMemo(
    () => buildPassengerManifest(
      passengers,
      Math.max(1, toNonNegativeInt(form.adults, 1)),
      toNonNegativeInt(form.children),
      toNonNegativeInt(form.infants)
    ),
    [form.adults, form.children, form.infants, passengers]
  );

  const hasExactAgencyMatch = useMemo(() => {
    if (!form.companyName.trim()) return false;
    return agencies.some((item) => item.name === form.companyName.trim());
  }, [agencies, form.companyName]);

  const setField = useCallback(<K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  }, []);

  const updatePassenger = useCallback((id: string, key: keyof AeroCrsPassengerRow, value: string) => {
    setPassengers((current) => current.map((row) => (row.id === id ? { ...row, [key]: value } : row)));
  }, []);

  const addPassenger = useCallback(() => {
    setPassengers((current) => [...current, makePassengerRow(current.length + 1)]);
    setForm((current) => ({ ...current, adults: current.adults + 1 }));
  }, []);

  const removePassenger = useCallback((id: string) => {
    setPassengers((current) => {
      if (current.length <= 1) return current;
      const next = current.filter((row) => row.id !== id);
      setForm((formState) => {
        if (formState.infants > 0) return { ...formState, infants: formState.infants - 1 };
        if (formState.children > 0) return { ...formState, children: formState.children - 1 };
        return { ...formState, adults: Math.max(1, formState.adults - 1) };
      });
      return next;
    });
  }, []);

  const searchFlights = useCallback(async () => {
    if (!form.from || !form.to || !form.departureDate) {
      setLastError('From, To, and Departure Date are required');
      return;
    }
    if (agencies.length && !hasExactAgencyMatch) {
      setLastError('Choose the exact AeroCRS company / agency before searching');
      return;
    }
    setIsSearching(true);
    try {
      const result = await aerocrsService.searchFlights({
        from: form.from,
        to: form.to,
        departureDate: form.departureDate,
        returnDate: form.returnDate || undefined,
        adults: Math.max(1, toNonNegativeInt(form.adults, 1)),
        children: toNonNegativeInt(form.children),
        infants: toNonNegativeInt(form.infants),
        companyName: form.companyName || undefined,
        chargeType: form.chargeType || undefined,
        currency: form.currency || undefined,
      });
      setSearchResults(result.flights || []);
      setLastSearchTime(result.searchedAt || new Date().toISOString());
      setSelectedFlight(null);
      setBooking(null);
      setSelectedBookingReference(null);
      setCustomerSearchResults([]);
      setSelectedCustomer(null);
      setLastError(null);
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Failed to search AeroCRS flights';
      setLastError(message);
    } finally {
      setIsSearching(false);
    }
  }, [agencies.length, form, hasExactAgencyMatch]);

  const clearSearch = useCallback(() => {
    const defaultAgency = agencies.find((item) => item.name === 'No Agency - Direct Client');
    setForm({
      from: '',
      to: '',
      departureDate: '',
      returnDate: '',
      adults: 1,
      children: 0,
      infants: 0,
      companyName: defaultAgency?.name || '',
      bookingName: '',
      flightType: 'SCHEDULED',
      chargeType: 'ADT',
      currency: 'USD',
    });
    setPassengers(buildPassengerRows(1));
    setSelectedFlight(null);
    setSearchResults([]);
    setBooking(null);
    setSelectedBookingReference(null);
    setLastSearchTime(null);
    setLastError(null);
    setLimitForm({
      ticketDate: '',
      ticketTime: '',
      paymentDate: '',
      paymentTime: '',
    });
  }, [agencies]);

  const selectFlight = useCallback((flight: AeroCrsFlight) => {
    const availableSeats = Number(flight.availableSeats ?? 0);
    const requiredSeats = seatBearingPassengers;
    if (availableSeats < requiredSeats) {
      const shortageMessage = availableSeats <= 0
        ? `Flight ${flight.flightNumber || ''} is sold out right now. Select a flight with available seats.`
        : `Flight ${flight.flightNumber || ''} only has ${availableSeats} seat(s), but ${requiredSeats} adult/child seat(s) are requested.`;
      setLastError(shortageMessage);
      return;
    }
    setSelectedFlight(flight);
    setBooking(null);
    setSelectedBookingReference(null);
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
    setSelectedCustomer(null);
    setLastError(null);
  }, [seatBearingPassengers]);

  const createDraft = useCallback(async () => {
    if (!selectedFlight) {
      setLastError('Select a flight before creating a booking draft');
      return;
    }
    if (!form.companyName.trim() || !form.bookingName.trim()) {
      setLastError('Company / Agency and Booking Name are required');
      return;
    }
    if (agencies.length && !hasExactAgencyMatch) {
      setLastError('Choose the exact AeroCRS company / agency before creating the draft');
      return;
    }
    const draftPassengers = passengerManifest.map(({ id, passengerType, roleIndex, roleLabel, requiresDateOfBirth, ...row }, index) => ({
      ...row,
      type: passengerType,
      firstName: row.firstName.trim(),
      lastName: row.lastName.trim(),
      dateOfBirth: row.dateOfBirth?.trim() || undefined,
      gender: row.gender?.trim() || undefined,
      nationality: row.nationality?.trim() || undefined,
      email: row.email?.trim() || undefined,
      phone: row.phone?.trim() || undefined,
      passportNumber: row.passportNumber?.trim() || undefined,
      name: [row.firstName.trim(), row.lastName.trim()].filter(Boolean).join(' ').trim() || `PAX ${index + 1}`,
    }));
    setIsCreatingDraft(true);
    try {
      const nextBooking = normalizeBookingReference(await aerocrsService.createBookingDraft({
        tripType: form.returnDate ? 'RT' : 'OW',
        companyName: form.companyName.trim(),
        bookingName: form.bookingName.trim(),
        flightType: form.flightType,
        from: form.from,
        to: form.to,
        departureDate: form.departureDate,
        returnDate: form.returnDate || undefined,
        adults: Math.max(1, toNonNegativeInt(form.adults, 1)),
        children: toNonNegativeInt(form.children),
        infants: toNonNegativeInt(form.infants),
        currency: form.currency || undefined,
        chargeType: form.chargeType,
        selectedFlight,
        passengers: draftPassengers,
      }));
      setBooking(nextBooking);
      setSelectedBookingReference(extractBookingReference(nextBooking) || null);
      setSelectedCustomer(nextBooking.customer || null);
      setCustomerSearchResults([]);
      if (extractBookingReference(nextBooking)) {
        setCustomerSearchQuery('');
      }
      setLastError(null);
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Failed to create booking draft';
      setLastError(message);
    } finally {
      setIsCreatingDraft(false);
    }
  }, [agencies.length, form, hasExactAgencyMatch, passengerManifest, selectedFlight]);

  const viewBooking = useCallback(async (reference?: string) => {
    const bookingReference = String(reference || selectedBookingReference || '').trim();
    if (!bookingReference) {
      setLastError('No booking reference selected');
      return;
    }
    setIsLoadingBooking(true);
    try {
      const result = normalizeBookingReference(await aerocrsService.getBooking(bookingReference));
      setBooking(result);
      setSelectedBookingReference(extractBookingReference(result) || null);
      setSelectedCustomer(result.customer || null);
      setLastError(null);
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Failed to load booking';
      setLastError(message);
    } finally {
      setIsLoadingBooking(false);
    }
  }, [selectedBookingReference]);

  const searchCustomers = useCallback(async () => {
    const bookingReference = String(selectedBookingReference || booking?.bookingReference || '').trim();
    const query = customerSearchQuery.trim();
    if (!bookingReference) {
      setLastError('Create or load a booking first');
      return;
    }
    if (!query) {
      setLastError('Enter a customer search term');
      return;
    }
    setIsSearchingCustomers(true);
    try {
      const results = await aerocrsService.searchBookingCustomers(bookingReference, query);
      setCustomerSearchResults(results);
      setLastError(null);
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Failed to search AeroCRS customers';
      setLastError(message);
    } finally {
      setIsSearchingCustomers(false);
    }
  }, [booking?.bookingReference, customerSearchQuery, selectedBookingReference]);

  const chooseCustomer = useCallback(async (customerId: string) => {
    const bookingReference = String(selectedBookingReference || booking?.bookingReference || '').trim();
    if (!bookingReference) {
      setLastError('Create or load a booking first');
      return;
    }
    setIsSelectingCustomer(true);
    try {
      const result = await aerocrsService.selectBookingCustomer(bookingReference, customerId);
      if (result.booking) {
        setBooking(result.booking);
        setSelectedBookingReference(result.booking.bookingReference);
      }
      setSelectedCustomer(result.selectedCustomer || null);
      setLastError(null);
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Failed to select AeroCRS customer';
      setLastError(message);
    } finally {
      setIsSelectingCustomer(false);
    }
  }, [booking?.bookingReference, selectedBookingReference]);

  const setLimitField = useCallback((key: keyof typeof limitForm, value: string) => {
    setLimitForm((current) => ({ ...current, [key]: value }));
  }, [limitForm]);

  const saveLimits = useCallback(async () => {
    const bookingReference = String(selectedBookingReference || booking?.bookingReference || '').trim();
    if (!bookingReference) {
      setLastError('Create or load a booking first');
      return;
    }

    const payload: {
      ticketTimeLimit?: { date: string; time: string };
      paymentTimeLimit?: { date: string; time: string };
    } = {};

    if (limitForm.ticketDate && limitForm.ticketTime) {
      payload.ticketTimeLimit = { date: limitForm.ticketDate, time: limitForm.ticketTime };
    }

    if (limitForm.paymentDate && limitForm.paymentTime) {
      payload.paymentTimeLimit = { date: limitForm.paymentDate, time: limitForm.paymentTime };
    }

    if (!payload.ticketTimeLimit && !payload.paymentTimeLimit) {
      setLastError('Enter a ticket time limit and/or payment time limit first');
      return;
    }

    setIsSavingLimits(true);
    try {
      const updated = normalizeBookingReference(await aerocrsService.updateBookingLimits(bookingReference, payload));
      setBooking(updated);
      setSelectedBookingReference(extractBookingReference(updated) || null);
      setLastError(null);
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Failed to update booking time limits';
      setLastError(message);
    } finally {
      setIsSavingLimits(false);
    }
  }, [booking?.bookingReference, limitForm, selectedBookingReference]);

  const validation = useMemo(() => {
    const expectedPassengers = totalPassengers;
    const trimmedPassengers = passengerManifest.map(({ id, passengerType, roleIndex, roleLabel, requiresDateOfBirth, ...row }) => ({
      ...row,
      firstName: row.firstName.trim(),
      lastName: row.lastName.trim(),
      dateOfBirth: row.dateOfBirth?.trim() || '',
      passengerType,
      requiresDateOfBirth,
    }));
    const namedPassengers = trimmedPassengers.filter((row) => row.firstName || row.lastName);
    const hasMissingNames = namedPassengers.some((row) => !row.firstName || !row.lastName);
    const missingBirthDateCount = trimmedPassengers.filter((row) => row.requiresDateOfBirth && !row.dateOfBirth).length;
    const passengerNamesReady = namedPassengers.length === expectedPassengers && !hasMissingNames && missingBirthDateCount === 0;
    const selectedFlightHasFareId = Boolean(selectedFlight?.fareId);
    const selectedFlightHasSeats = Number(selectedFlight?.availableSeats ?? 0) >= seatBearingPassengers;
    const canCreateDraft = Boolean(
      status?.configured
      && selectedFlight
      && selectedFlightHasFareId
      && form.companyName.trim()
      && form.bookingName.trim()
      && passengerNamesReady
      && selectedFlightHasSeats
    );
    return {
      expectedPassengers,
      seatBearingPassengers,
      agencyExactMatch: hasExactAgencyMatch,
      namedPassengersCount: namedPassengers.length,
      missingBirthDateCount,
      passengerNamesReady,
      selectedFlightHasFareId,
      selectedFlightHasSeats,
      hasMissingNames,
      canCreateDraft,
    };
  }, [form, hasExactAgencyMatch, passengerManifest, seatBearingPassengers, selectedFlight, status, totalPassengers]);

  return {
    state: {
      status,
      agencies,
      agencyOptions,
      destinations,
      destinationOptions,
      isLoadingStatus,
      isSearching,
      isCreatingDraft,
      isLoadingBooking,
      isSearchingCustomers,
      isSelectingCustomer,
      isSavingLimits,
      lastSearchTime,
      selectedBookingReference,
      selectedFlight,
      searchResults,
      booking,
      customerSearchQuery,
      customerSearchResults,
      selectedCustomer,
      form,
      passengers,
      passengerManifest,
      totalPassengers,
      seatBearingPassengers,
      lastError,
      limitForm,
      validation,
    },
    actions: {
      refreshStatus: loadStatus,
      setField,
      updatePassenger,
      addPassenger,
      removePassenger,
      searchFlights,
      clearSearch,
      selectFlight,
      createDraft,
      viewBooking,
      setCustomerSearchQuery,
      searchCustomers,
      chooseCustomer,
      setLimitField,
      saveLimits,
    },
  };
};
